import type { Row, SortingState } from "@tanstack/react-table";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import clsx from "clsx";
import type { FunctionComponent, MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import Table from "~/components/Table";
import { INVOICE_DETAILS_PATH_WITH_ORIGIN } from "~/constants/appRoutes.ts";
import { INVOICES_IMPORT_ERRORS_PAGE_KEY, INVOICES_SHARED_FILTER_VALUES } from "~/constants/pageStateStorageKeys.ts";
import usePageStateStorage from "~/hooks/usePageStateStorage";
import {
	applyDeliveryPeriodFilter, castFilterDatesToDate,
} from "~/modules/billing/components/DeliveryPeriodFilter/deliveryPeriodFilterUtils.ts";
import {
	validInvoiceDetailsNavigatedFromOrigins,
} from "~/modules/billing/components/InvoiceDetailsView/invoiceDetailsUtils.ts";
import InvoicePageHeader from "~/modules/billing/components/InvoicePageHeader";
import {
	processedInvoicesIndexTableColumnDefs,
} from "~/modules/billing/components/ProcessedInvoicesIndexView/processedInvoiceIndexTableColumnDefs.tsx";
import type {
	InvoiceIndexTableData,
} from "~/modules/billing/components/UnpaidInvoicesIndexView/unpaidInvoicesIndexTableTypes.ts";
import {
	InvoicePageTabId,
	invoiceTablesDefaultPageState,
	invoiceTableSharedFilterDefaultPageState,
} from "~/modules/billing/utils/billingUtils.ts";
import type { DateRangeNullable } from "~/types/dateAndTimeTypes.ts";

type Props = {
	isLoading: boolean;
	tableData: InvoiceIndexTableData[];
};

const ProcessedErroneousInvoicesIndexView: FunctionComponent<Props> = ({ isLoading, tableData }) => {
	const { pageState, setPageState } = usePageStateStorage({
		pageKey: INVOICES_IMPORT_ERRORS_PAGE_KEY,
		defaultState: invoiceTablesDefaultPageState,
	});

	const { pageState: sharedFilterPageState, setPageState: setSharedFilterPageState } = usePageStateStorage({
		pageKey: INVOICES_SHARED_FILTER_VALUES,
		defaultState: invoiceTableSharedFilterDefaultPageState,
	});
	const [deliveryPeriodFilter, setDeliveryPeriodFilter] = useState<DateRangeNullable>(castFilterDatesToDate(sharedFilterPageState.deliveryPeriodFilter));
	const [globalFilter, setGlobalFilter] = useState<string>(sharedFilterPageState.globalFilter);
	const [sorting, setSorting] = useState<SortingState>(pageState.sorting);

	useEffect(() => {
		setPageState({ sorting });
	}, [setPageState, sorting]);

	useEffect(() => {
		setSharedFilterPageState({ globalFilter, deliveryPeriodFilter });
	}, [deliveryPeriodFilter, globalFilter, setSharedFilterPageState]);

	const navigate = useNavigate();

	const preFilteredTableData = useMemo(() => {
		return applyDeliveryPeriodFilter(deliveryPeriodFilter, tableData);
	}, [tableData, deliveryPeriodFilter]);

	const handleRowClick = useCallback(
		(row: Row<InvoiceIndexTableData>, event: MouseEvent) => {
			const path = INVOICE_DETAILS_PATH_WITH_ORIGIN(row.original.id, validInvoiceDetailsNavigatedFromOrigins.PROCESSED_INVOICES_INDEX);

			if (event.ctrlKey || event.metaKey) {
				window.open(path, "_blank");
			} else {
				navigate(path);
			}
		},
		[navigate],
	);

	const table = useReactTable({
		data: preFilteredTableData,
		columns: processedInvoicesIndexTableColumnDefs,
		onSortingChange: setSorting,
		state: {
			globalFilter,
			sorting,
		},
		meta: {
			onRowClick: handleRowClick,
		},
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		sortDescFirst: true,
	});

	return <div className={clsx("auto-grid-2-rows mx-auto h-screen px-8")}>
		<InvoicePageHeader activeTabId={InvoicePageTabId.PROCESSED_WITH_ERRORS}
						   deliveryPeriodFilter={deliveryPeriodFilter}
						   globalFilter={globalFilter}
						   setDeliveryPeriodFilter={setDeliveryPeriodFilter}
						   setGlobalFilter={setGlobalFilter}
						   userCanManageInvoices={true}
		>
		</InvoicePageHeader>

		<div className="h-full overflow-hidden pb-10">
			<Table table={table}
				   isLoading={isLoading}
				   fixedWidth={true} />
		</div>
	</div>;

};

export default ProcessedErroneousInvoicesIndexView;